/* eslint-disable react/no-unescaped-entities */
/**
 * /verifier — Vérificateur CONFILEX V3 avec paywall serveur strict.
 *
 * Modes :
 *   - idle / uploading / analyzing
 *   - locked : aperçu seulement (nombre d'anomalies, indicateur, 1 teaser)
 *              + sélection palier 29/49/89€ → Stripe Checkout
 *   - unlocked : rapport complet, PDF téléchargeable
 *
 * Sécurité business : le contenu détaillé est tenu côté serveur jusqu'à
 * validation du paiement. Le navigateur ne reçoit JAMAIS analysis.key_facts,
 * extraction.amounts_eur, etc. tant que `paid !== true`.
 */

import { useEffect, useRef, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  FileText, CheckCircle2, Loader2, ShieldCheck, Trash2, Download,
  Receipt, Home, FileSpreadsheet, Landmark, ArrowRight, AlertCircle, Lock,
  Sparkles, ScanLine, Highlighter, CalendarDays, Users, FileStack, Fingerprint,
  Copy as CopyIcon, History, GitMerge, ShieldAlert,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { apiClient } from "@/lib/api";
import AIDisclaimer from "@/components/AIDisclaimer";
import ConfilexHourglass from "@/components/ConfilexHourglass";
import BookingNavbar from "@/components/booking/BookingNavbar";
import Footer from "@/components/Footer";
import Seo, { ldVerifierProduct, ldBreadcrumb, ldFAQ } from "@/components/Seo";
import TraceabilityBanner from "@/components/TraceabilityBanner";
import VerifierHandoffPanel from "@/components/VerifierHandoffPanel";
import VerifierHistoryPanel from "@/components/VerifierHistoryPanel";
import EnvelopeDropIcon from "@/components/EnvelopeDropIcon";
import { useAuth } from "@/context/AuthContext";
import { useStepUp } from "@/hooks/useStepUp";

const ICONS = { Receipt, Home, FileSpreadsheet, Landmark, FileText };
const STORAGE_KEY = "confilex_verifier_token";

export default function Verifier() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [types, setTypes] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [selectedVertical, setSelectedVertical] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [session, setSession] = useState(null);
  const [phase, setPhase] = useState("idle"); // idle | uploading | analyzing | unlocking | result
  const [error, setError] = useState(null);
  const [progressLabel, setProgressLabel] = useState("");
  const fileInputRef = useRef(null);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  // Catalogues
  useEffect(() => {
    apiClient.get("/verifier/document-types")
      .then((r) => setTypes(r.data.types || []))
      .catch(() => setError("Impossible de charger les types de documents."));
    apiClient.get("/verifier/pricing")
      .then((r) => setTiers(r.data.tiers || []))
      .catch(() => { /* non-bloquant : ignoré volontairement */ });
    apiClient.get("/verifier/verticals")
      .then((r) => setVerticals(r.data.modules || []))
      .catch(() => { /* non-bloquant : ignoré volontairement */ });
  }, []);

  // Retour Stripe / ADMIN_TEST : ?token=...&paid=1 — on recharge l'état
  useEffect(() => {
    const tokenFromUrl = searchParams.get("token");
    const paid = searchParams.get("paid");
    if (tokenFromUrl && paid === "1") {
      // Sablier visible pendant qu'on va chercher le rapport complet
      setPhase("unlocking");
      (async () => {
        try {
          const r = await apiClient.get(`/verifier/${tokenFromUrl}`);
          setSession(r.data);
          setPhase("result");
          localStorage.setItem(STORAGE_KEY, tokenFromUrl);
          // Nettoie l'URL
          setSearchParams({}, { replace: true });
        } catch (e) {
          setError(e?.response?.data?.detail || "Impossible de charger le rapport débloqué.");
          setPhase("idle");
        }
      })();
    } else if (tokenFromUrl && searchParams.get("cancel") === "1") {
      apiClient.get(`/verifier/${tokenFromUrl}`).then((r) => {
        setSession(r.data); setPhase("result");
        setSearchParams({}, { replace: true });
      }).catch(() => { /* non-bloquant : ignoré volontairement */ });
    } else if (!session) {
      // Reprise locale si l'utilisateur recharge la page.
      // - Si status === "completed" : affiche directement le rapport
      // - Si status === "processing" : relance le polling (l'analyse
      //   continue côté backend, on retrouve le même job sans re-facturer)
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        apiClient.get(`/verifier/${saved}`)
          .then(async (r) => {
            const st = r.data?.status;
            if (st === "completed") {
              setSession(r.data);
              setPhase("result");
            } else if (st === "processing") {
              // Reprise du polling — même token, aucun risque de doublon
              setPhase("analyzing");
              setProgressLabel("Reprise de l'analyse en cours…");
              const MAX_POLLS = 200;
              const INTERVAL_MS = 3000;
              const MAX_CONSECUTIVE_ERRORS = 30;
              let consecutiveErrors = 0;
              for (let i = 0; i < MAX_POLLS; i += 1) {
                await new Promise((res) => setTimeout(res, INTERVAL_MS));
                try {
                  const poll = await apiClient.get(`/verifier/${saved}`);
                  consecutiveErrors = 0;
                  if (poll.data?.status === "completed") {
                    setSession(poll.data);
                    setPhase("result");
                    return;
                  }
                  if (poll.data?.status === "failed") {
                    setError(poll.data?.analysis_error || "L'analyse a échoué.");
                    setPhase("idle");
                    return;
                  }
                } catch (pollErr) {
                  const isNetworkErr = !pollErr?.response;
                  const isServerErr = pollErr?.response?.status >= 500;
                  if (!isNetworkErr && !isServerErr) {
                    setError("Une erreur est survenue lors de la reprise.");
                    setPhase("idle");
                    return;
                  }
                  consecutiveErrors += 1;
                  if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
                    setError("Connexion instable. Rechargez la page dans un instant.");
                    setPhase("idle");
                    return;
                  }
                  setProgressLabel(`Reprise de la connexion… (${consecutiveErrors}/${MAX_CONSECUTIVE_ERRORS})`);
                }
              }
            } else if (st === "failed") {
              localStorage.removeItem(STORAGE_KEY);
            }
            // status === "created" ou autre → on ignore
          })
          .catch(() => localStorage.removeItem(STORAGE_KEY));
      }
    }
   
  }, [searchParams]);

  const onFileChosen = async (file) => {
    if (!file) return;
    setError(null);
    setPhase("uploading");
    setProgressLabel("Création de la session sécurisée…");
    try {
      const s = await apiClient.post("/verifier/sessions", {});
      const token = s.data.session_token;
      localStorage.setItem(STORAGE_KEY, token);

      setProgressLabel("Envoi du document chiffré…");
      const form = new FormData();
      form.append("file", file);
      if (selectedType) form.append("doc_type_hint", selectedType);
      if (selectedVertical) form.append("vertical_id", selectedVertical);

      setPhase("analyzing");
      setProgressLabel("Analyse factuelle en cours…");
      const r = await apiClient.post(`/verifier/${token}/upload`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // L'upload retourne immédiatement (status "processing").
      // L'analyse LLM tourne en background sur le backend (thread OS séparé).
      // Le frontend récupère l'état via polling résilient sur GET /verifier/{token}.
      if (r.data?.status === "completed") {
        setSession(r.data);
        setPhase("result");
        return;
      }

      // ============================================================
      // POLLING RÉSILIENT (résiste aux erreurs réseau transitoires)
      // ============================================================
      // - Toutes les 3 s pendant max 10 min
      // - Chaque poll est try/catch INDIVIDUEL : une erreur transitoire
      //   (Network Error, 502, 504, ECONNRESET) ne fait PLUS échouer le
      //   parcours. On loggue le compteur d'échecs consécutifs.
      // - N'affiche « perte de connexion » qu'après 5 échecs successifs.
      // - Le job backend continue en background : aucun risque de perte,
      //   pas de re-facturation, pas de double analyse. Le même token est
      //   ré-utilisé jusqu'à ce que le status devienne "completed".
      const MAX_POLLS = 200;             // 10 min max (200 × 3s)
      const INTERVAL_MS = 3000;
      const MAX_CONSECUTIVE_ERRORS = 30;  // ~90s (couvre un cold-start de deploy)
      let consecutiveErrors = 0;

      for (let i = 0; i < MAX_POLLS; i += 1) {
        await new Promise((res) => setTimeout(res, INTERVAL_MS));
        try {
          const poll = await apiClient.get(`/verifier/${token}`);
          consecutiveErrors = 0;
          if (poll.data?.status === "completed") {
            setSession(poll.data);
            setPhase("result");
            return;
          }
          if (poll.data?.status === "failed") {
            throw new Error(poll.data?.analysis_error || "L'analyse a échoué. Merci de réessayer.");
          }
          // toujours en processing → on continue à poller silencieusement
          if (consecutiveErrors === 0) {
            setProgressLabel("Analyse factuelle en cours…");
          }
        } catch (pollErr) {
          // Erreur de polling — on distingue « status failed » (fatal)
          // d'une simple erreur réseau (retryable)
          const isNetworkErr = !pollErr?.response;
          const isServerErr = pollErr?.response?.status >= 500;
          if (!isNetworkErr && !isServerErr) {
            // 4xx (403, 404…) = erreur définitive, on ne réessaie pas
            throw pollErr;
          }
          consecutiveErrors += 1;
          if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
            throw new Error(
              "Connexion instable — l'analyse continue en arrière-plan. "
              + "Rechargez la page dans quelques instants, votre document sera prêt.",
            );
          }
          setProgressLabel(
            `Reprise de la connexion… (tentative ${consecutiveErrors}/${MAX_CONSECUTIVE_ERRORS})`,
          );
          // Boucle continue → retry au prochain intervalle
        }
      }
      throw new Error(
        "L'analyse prend plus de temps que prévu. Rechargez la page dans quelques instants.",
      );
    } catch (e) {
      // Message convivial : jamais « Network Error » brut
      const rawMsg = e?.response?.data?.detail || e?.message || "";
      const friendly = rawMsg === "Network Error"
        ? "Difficulté temporaire de connexion. Confilex a lancé votre analyse — rechargez la page dans quelques instants."
        : (rawMsg || "Une erreur est survenue lors de l'analyse.");
      setError(friendly);
      setPhase("idle");
    }
  };

  const handlePurge = async () => {
    if (!session?.session_token) return;
    if (!window.confirm("Confirmer la suppression immédiate de cette analyse ? Action irréversible.")) return;
    try {
      await apiClient.delete(`/verifier/${session.session_token}`);
      localStorage.removeItem(STORAGE_KEY);
      setSession(null); setSelectedType(null); setPhase("idle");
    } catch (e) {
      setError("Suppression impossible.");
    }
  };

  const handleReset = () => {
    localStorage.removeItem(STORAGE_KEY);
    setSession(null); setSelectedType(null); setPhase("idle");
  };

  return (
    <>
      <Seo
        title="CONFILEX — Audit Documentaire Intelligent (bulletin de paie, bail, facture, avis d'imposition)"
        description="Audit documentaire intelligent par IA. Faites analyser vos documents par le Vérificateur CONFILEX — 3 niveaux d'analyse à 29 €, 39 € et 49 € TTC. Analyse factuelle, détection de divergences, points à clarifier."
        canonical="/verifier"
        keywords={[
          "audit documentaire intelligent", "audit documentaire", "vérification document",
          "analyse bulletin de paie", "vérification bail", "analyse facture", "audit avis d'imposition",
          "IA documentaire", "score de complétude", "détection divergences documentaires",
          "rapport factuel", "audit pré-décisionnel France",
        ]}
        jsonLd={[
          ldVerifierProduct(),
          ldBreadcrumb([
            { name: "Accueil", url: "/" },
            { name: "Audit Documentaire Intelligent", url: "/verifier" },
          ]),
          ldFAQ([
            { q: "L'Audit Documentaire CONFILEX donne-t-il un avis juridique ?", a: "Non. CONFILEX réalise une analyse documentaire, factuelle et organisationnelle. Le rapport ne constitue ni une consultation juridique, ni un avis juridique, ni une consultation comptable, fiscale, notariale ou financière. Toute interprétation professionnelle doit être appréciée par un professionnel compétent." },
            { q: "Combien coûte une analyse ?", a: "Trois niveaux d'analyse TTC sont proposés : 29 € Analyse Essentielle (résumé structuré, faits clés, incohérences apparentes, pièces manquantes), 39 € Analyse Approfondie (tout l'Essentiel + chronologie automatique + contradictions internes + dates importantes + suggestions de documents complémentaires), 49 € Analyse Premium précontentieuse (tout l'Approfondie + analyse multi-documents + croisement des informations + tableaux d'incohérences et de pièces utiles + préparation à la transmission à un spécialiste référencé)." },
            { q: "Quels documents puis-je analyser ?", a: "Bulletin de paie, contrat de bail (habitation et commercial), facture / devis, avis d'imposition. D'autres types seront ajoutés progressivement." },
            { q: "Mon document est-il utilisé pour entraîner une IA ?", a: "Non. Aucun document transmis n'est utilisé pour entraîner un modèle d'intelligence artificielle. Vous pouvez purger votre analyse en un clic (droit RGPD à l'effacement)." },
            { q: "Combien de temps prend l'analyse ?", a: "Environ 15 secondes pour un document PDF avec couche texte." },
            { q: "Que dois-je faire après avoir reçu mon analyse ?", a: "L'analyse liste les questions à préparer pour votre professionnel (avocat, expert-comptable, notaire ou conseiller). L'Analyse Premium précontentieuse (49 €) optimise la préparation du dossier pour transmission à un spécialiste référencé." },
          ]),
        ]}
      />

      <BookingNavbar />

      <main className="min-h-screen bg-ivory">
        <section className="border-b border-sage bg-paper">
          <div className="max-w-5xl mx-auto px-6 py-14 sm:py-20">
            <p className="text-[10px] uppercase tracking-[0.32em] text-copper mb-3" data-testid="verifier-kicker">
              CONFILEX · AUDIT DOCUMENTAIRE INTELLIGENT
            </p>
            <h1 className="font-serif italic text-4xl sm:text-5xl lg:text-6xl text-night leading-[1.05] mb-5" data-testid="verifier-title">
              Organisez, vérifiez et exploitez vos documents<br />avant toute consultation professionnelle.
            </h1>
            <p className="text-base sm:text-lg text-night/75 max-w-2xl leading-relaxed">
              CONFILEX réalise en quelques secondes une <b>analyse documentaire, factuelle et organisationnelle</b> des éléments transmis —
              chronologie, inventaire des pièces, divergences documentaires, points à clarifier, questions à préparer pour un professionnel.
              <span className="block mt-3 text-[13px] text-night/60 italic">
                Le rapport ne constitue ni une consultation juridique, ni un avis juridique, ni une consultation comptable, fiscale, notariale ou financière.
              </span>
            </p>
          </div>
        </section>

        <section className="max-w-5xl mx-auto px-6 py-12 sm:py-16">
          {phase === "idle" && !session && (
            <IdleView types={types} selectedType={selectedType} setSelectedType={setSelectedType}
                      verticals={verticals} selectedVertical={selectedVertical} setSelectedVertical={setSelectedVertical}
                      onFile={onFileChosen} inputRef={fileInputRef} error={error} />
          )}

          {(phase === "uploading" || phase === "analyzing") && (
            <ConfilexHourglass
              testid="verifier-hourglass"
              size="lg"
              stage={phase === "uploading" ? "preparation" : "analysis"}
              message={
                phase === "uploading"
                  ? "Sécurisation et envoi du document…"
                  : "Analyse documentaire en cours (≈ 15 s)"
              }
              substeps={phase === "uploading" ? [
                "Création de la session sécurisée",
                "Chiffrement local du document",
                "Transmission vers le moteur d'analyse",
              ] : [
                "Lecture du document",
                "Identification des parties et acteurs",
                "Analyse des dates et montants",
                "Détection des incohérences",
                "Structuration du dossier",
                "Génération du rapport",
              ]}
            />
          )}

          {phase === "unlocking" && (
            <ConfilexHourglass
              testid="verifier-unlocking"
              size="lg"
              stage="finalization"
              message="Préparation de votre rapport complet…"
              substeps={[
                "Validation du paiement",
                "Composition du rapport détaillé",
                "Chargement des passages surlignés et de la chronologie",
              ]}
              error={error}
              onRetry={() => { setError(null); setPhase("idle"); }}
            />
          )}

          {phase === "result" && session && (
            session.paid
              ? <UnlockedReport session={session} onPurge={handlePurge} onReset={handleReset} />
              : <LockedReport
                  session={session}
                  tiers={tiers}
                  onPurge={handlePurge}
                  onReset={handleReset}
                  isAdmin={isAdmin}
                  onAdminTestUnlock={async (updatedSession) => {
                    setSession(updatedSession);
                    setPhase("result");
                  }}
                  onEnterUnlocking={() => setPhase("unlocking")}
                />
          )}
        </section>

        <section className="border-t border-sage bg-paper">
          <div className="max-w-5xl mx-auto px-6 py-10">
            <AIDisclaimer variant="full" />
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}

// ---------------------------------------------------------------------------
function IdleView({ types, selectedType, setSelectedType, verticals, selectedVertical, setSelectedVertical, onFile, inputRef, error }) {
  const [dragOver, setDragOver] = useState(false);
  return (
    <>
      {/* Step 0 : sélection du module vertical (optionnel mais recommandé) */}
      {verticals && verticals.length > 0 && (
        <>
          <h2 className="font-serif italic text-2xl text-night mb-2" data-testid="verifier-vertical-heading">
            Choisissez votre domaine d'audit (optionnel)
          </h2>
          <p className="text-sm text-night/65 mb-4">
            Sélectionnez un module d'audit spécialisé pour une lecture documentaire ciblée.
            Si vous ne choisissez pas, la lecture restera générique.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 mb-10" data-testid="verifier-verticals-grid">
            <button
              type="button"
              onClick={() => setSelectedVertical(null)}
              data-testid="vertical-none"
              className={`text-left p-3 rounded-sm border transition-smooth ${
                !selectedVertical
                  ? "border-copper bg-paper shadow-sm"
                  : "border-sage bg-ivory hover:border-copper"
              }`}
            >
              <p className="text-[11px] tracking-[0.18em] uppercase text-copper font-semibold">Générique</p>
              <p className="text-[12px] text-night/70 mt-1">Lecture documentaire standard</p>
            </button>
            {verticals.map((v) => {
              const active = selectedVertical === v.id;
              return (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => setSelectedVertical(active ? null : v.id)}
                  data-testid={`vertical-${v.id}`}
                  title={v.tagline}
                  className={`text-left p-3 rounded-sm border transition-smooth ${
                    active
                      ? "border-copper bg-paper shadow-sm"
                      : "border-sage bg-ivory hover:border-copper"
                  }`}
                >
                  <p className={`text-[11px] tracking-[0.18em] uppercase font-semibold ${
                    active ? "text-copper" : "text-petrol"
                  }`}>
                    {v.label.replace("Audit ", "")}
                  </p>
                  <p className="text-[12px] text-night/70 mt-1 leading-snug line-clamp-2">{v.tagline}</p>
                </button>
              );
            })}
          </div>
        </>
      )}

      <h2 className="font-serif italic text-2xl text-night mb-5" data-testid="verifier-step1-heading">
        1. Choisissez le type de document
      </h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-12">
        {types.map((t) => {
          const Icon = ICONS[t.icon] || FileText;
          const active = selectedType === t.code;
          return (
            <button
              key={t.code}
              type="button"
              onClick={() => setSelectedType(active ? null : t.code)}
              data-testid={`verifier-type-${t.code}`}
              className={`group text-left p-4 rounded-sm border transition-smooth ${
                active
                  ? "border-copper bg-paper shadow-sm"
                  : "border-sage bg-ivory hover:border-copper hairline-copper"
              }`}
            >
              <Icon className={`w-5 h-5 mb-2 ${active ? "text-copper" : "text-petrol"}`} />
              <p className="text-sm font-semibold text-night">{t.label}</p>
              <p className="text-[11px] text-night/55 mt-1 leading-snug">{t.description}</p>
            </button>
          );
        })}
      </div>

      <h2 className="font-serif italic text-2xl text-night mb-5" data-testid="verifier-step2-heading">
        2. Déposez vos documents pour lancer leur analyse {selectedType ? "" : "(le type est détecté automatiquement)"}
      </h2>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer?.files?.[0]; if (f) onFile(f); }}
        onClick={() => inputRef.current?.click()}
        data-testid="verifier-dropzone"
        className={`group cursor-pointer relative border border-dashed rounded-sm p-10 sm:p-12 text-center transition-smooth ${
          dragOver
            ? "border-[#C9A961] bg-paper shadow-[0_18px_60px_-22px_rgba(201,169,97,0.45)]"
            : "border-sage bg-ivory hover:border-[#C9A961]/70 hover:bg-paper"
        }`}
      >
        <input ref={inputRef} type="file" accept="application/pdf,.pdf,.txt"
               onChange={(e) => onFile(e.target.files?.[0])} className="hidden"
               data-testid="verifier-file-input" />

        {/* Visuel principal : enveloppe institutionnelle CONFILEX */}
        <div className="flex justify-center mb-4">
          <EnvelopeDropIcon active={dragOver} size={132} />
        </div>

        {/* Message contextuel — switche en drag-over */}
        <p
          className="font-medium transition-colors"
          style={{ color: dragOver ? "#0B3A2A" : undefined }}
          data-testid="verifier-dropzone-label"
        >
          {dragOver ? "Déposez vos documents — l'analyse démarre immédiatement" : "Ajoutez vos documents pour lancer leur analyse"}
        </p>
        <p className="text-[12px] text-night/55 mt-2">PDF avec couche texte · 15 Mo max · 30 pages max · Analyse en ≈ 15 s</p>
        <p className="text-[11px] text-night/45 mt-3 italic flex items-center justify-center gap-1.5">
          <ShieldCheck className="w-3 h-3" aria-hidden="true" />
          Transmission chiffrée · Aucune donnée n'est utilisée pour entraîner un modèle d'IA.
        </p>
        <p className="text-[11px] text-night/45 mt-1 italic" data-testid="verifier-ownership-mention">
          Les documents transmis demeurent la propriété exclusive de leur auteur ou de leur titulaire de droits.
        </p>
      </div>

      {error && (
        <Card className="mt-5 p-4 bg-paper border-bordeaux/30">
          <div className="flex gap-2 text-bordeaux text-sm">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <span data-testid="verifier-error">{error}</span>
          </div>
        </Card>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
function LockedReport({ session, tiers, onPurge, onReset, isAdmin = false, onAdminTestUnlock, onEnterUnlocking }) {
  // Sélection par défaut : palier recommandé (Premium précontentieuse) si présent.
  const defaultTier = (tiers.find((t) => t.recommended)?.code) || "approfondi";
  const [selectedTier, setSelectedTier] = useState(defaultTier);
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [waiverAccepted, setWaiverAccepted] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const preview = session.preview || {};
  const vig = preview.vigilance_preview || {};
  const counts = preview.counts || {};

  const handleUnlock = async (adminTest = false) => {
    if (!consentAccepted || !waiverAccepted) return;
    setLoading(true); setErr(null);
    try {
      const r = await apiClient.post(`/verifier/${session.session_token}/checkout`, {
        tier: selectedTier,
        origin_url: window.location.origin,
        consent_accepted: true,
        waiver_14_days_accepted: true,
        customer_email: email || null,
        admin_test: !!adminTest,
      });
      // ADMIN_TEST : pas de redirection Stripe — le rapport est déjà débloqué.
      // On bascule immédiatement sur le sablier puis on charge le rapport complet.
      if (r.data?.admin_test) {
        if (onEnterUnlocking) onEnterUnlocking();
        try {
          const state = await apiClient.get(`/verifier/${session.session_token}`);
          if (onAdminTestUnlock) onAdminTestUnlock(state.data);
        } catch (e) {
          setErr(e?.response?.data?.detail || "Impossible de charger le rapport débloqué.");
          setLoading(false);
        }
        return;
      }
      // Parcours Stripe normal
      window.location.href = r.data.url;
    } catch (e) {
      setErr(e?.response?.data?.detail || "Création du paiement impossible.");
      setLoading(false);
    }
  };

  // Block copy/select on locked content
  const blockCopy = useCallback((e) => { e.preventDefault(); return false; }, []);

  return (
    <div className="space-y-6 select-none" data-testid="verifier-result" onCopy={blockCopy} onCut={blockCopy} onContextMenu={blockCopy}>
      <Card className="p-4 bg-paper border-copper/30 text-[12px] text-night/75 italic leading-relaxed">
        {session.disclaimer}
      </Card>
      <TraceabilityBanner />

      {/* En-tête analyse */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-[0.28em] text-copper mb-1">Analyse terminée · Rapport verrouillé</p>
          <h2 className="font-serif italic text-2xl text-night" data-testid="verifier-result-title">
            {session.doc_type_label} — {session.filename}
          </h2>
          <FingerprintBadge fingerprint={session.fingerprint} />
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={onPurge} data-testid="verifier-purge-btn"
                  className="rounded-sm border-bordeaux/30 text-bordeaux hover:bg-bordeaux/5">
            <Trash2 className="w-4 h-4 mr-2" /> Purger
          </Button>
          <Button variant="outline" onClick={onReset} data-testid="verifier-new-btn" className="rounded-sm">
            Nouvelle analyse
          </Button>
        </div>
      </div>

      {/* Aperçu indicateur */}
      <Card className="p-5 bg-paper border-copper/30" data-testid="verifier-vigilance">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-copper mb-1">Indicateur de complétude documentaire</p>
            <p className="font-serif italic text-xl text-night">{vig.label || "—"}</p>
          </div>
          <p className="font-serif italic text-3xl text-night">{vig.score ?? "—"}<span className="text-base text-night/55">/100</span></p>
        </div>
        <div className="h-1 bg-sage/60 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-petrol to-copper transition-all"
               style={{ width: `${vig.score || 0}%` }} />
        </div>
      </Card>

      {/* Compteurs anomalies */}
      <Card className="p-6 bg-night text-ivory border-none" data-testid="verifier-counts">
        <p className="text-[10px] uppercase tracking-[0.28em] text-copper mb-3">Détecté dans votre document</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Stat n={counts.highlights || 0} l="Passages surlignés" />
          <Stat n={counts.timeline || 0} l="Dates clés" />
          <Stat n={counts.actors || 0} l="Acteurs identifiés" />
          <Stat n={counts.documents_mentioned || 0} l="Documents cités" />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4 pt-4 border-t border-ivory/10">
          <Stat n={counts.total_attention_points || 0} l="Points d'attention" />
          <Stat n={counts.key_facts || 0} l="Faits clés" />
          <Stat n={counts.missing_info || 0} l="Infos manquantes" />
          <Stat n={counts.questions_for_expert || 0} l="Questions pour spécialiste" />
        </div>
      </Card>

      {/* Teaser flouté */}
      <Card className="relative overflow-hidden p-6 bg-paper border-copper/30">
        <div className="flex items-start gap-3 mb-3">
          <ScanLine className="w-5 h-5 text-copper shrink-0 mt-0.5" />
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-copper mb-1">Aperçu</p>
            <p className="text-sm text-night/85 leading-relaxed">{preview.intro}</p>
          </div>
        </div>
        {preview.teaser_fact && (
          <div className="mt-4 p-4 rounded-sm bg-ivory border border-sage">
            <p className="text-[11px] uppercase tracking-[0.2em] text-night/45 mb-1">Premier fait extrait</p>
            <p className="text-sm text-night italic">« {preview.teaser_fact} »</p>
          </div>
        )}
        {/* Lignes floutées simulant le contenu */}
        <div className="mt-4 space-y-2" aria-hidden="true">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-3 rounded-full bg-sage/50"
                 style={{ width: `${85 - i * 8}%`, filter: "blur(2px)" }} />
          ))}
        </div>
        <div className="mt-3 inline-flex items-center gap-2 text-[12px] text-night/55 italic">
          <Lock className="w-3.5 h-3.5" />
          Analyse détaillée disponible après déblocage.
        </div>
      </Card>

      {/* Paywall — sélection palier */}
      <Card className="p-6 bg-ivory border-copper">
        <div className="flex items-center gap-3 mb-2">
          <Sparkles className="w-5 h-5 text-copper" />
          <p className="text-[10px] uppercase tracking-[0.28em] text-copper">Faites analyser vos documents par le Vérificateur CONFILEX</p>
        </div>
        <h3 className="font-serif italic text-2xl text-night mb-1">Choisissez votre niveau d'analyse</h3>
        <p className="text-[13px] text-night/65 mb-2">
          Paiement sécurisé Stripe · TVA 20 % incluse · Génération immédiate après paiement.
        </p>
        <p className="text-[12px] text-night/55 mb-5 italic">
          Le Vérificateur CONFILEX permet de structurer et d'organiser vos documents afin d'identifier les éléments importants de votre dossier avant consultation éventuelle d'un professionnel.
        </p>

        <div className="grid md:grid-cols-3 gap-3 mb-6">
          {tiers.map((t) => {
            const active = selectedTier === t.code;
            return (
              <button
                key={t.code}
                type="button"
                onClick={() => setSelectedTier(t.code)}
                data-testid={`verifier-tier-${t.code}`}
                className={`relative text-left p-4 rounded-sm border-2 transition-smooth ${
                  active ? "border-copper bg-paper shadow-[0_18px_60px_-25px_rgba(196,164,104,0.45)]" : "border-sage bg-ivory hover:border-copper"
                }`}
              >
                {t.recommended && (
                  <span
                    className="absolute -top-2.5 right-3 px-2 py-0.5 rounded-full bg-copper text-paper text-[9px] uppercase tracking-[0.2em] font-medium shadow"
                    data-testid={`verifier-tier-${t.code}-badge`}
                  >
                    La plus complète
                  </span>
                )}
                <p className="text-[10px] uppercase tracking-[0.24em] text-copper mb-1">{t.label}</p>
                <p className="font-serif italic text-2xl text-night mb-2">{t.price_display}</p>
                <p className="text-[12px] text-night/70 leading-snug mb-3">{t.description}</p>
                <ul className="space-y-1">
                  {(t.features || []).slice(0, 5).map((f, i) => (
                    <li key={i} className="text-[11px] text-night/70 flex gap-1.5">
                      <CheckCircle2 className="w-3 h-3 text-copper mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                {active && t.cta_label && (
                  <p className="mt-3 text-[10px] uppercase tracking-[0.22em] text-night/55 italic">
                    Sélectionné · {t.cta_label}
                  </p>
                )}
              </button>
            );
          })}
        </div>

        {/* Mention de conformité — visible avant paiement */}
        <div className="mb-5 p-3.5 rounded-sm bg-paper border border-night/10" data-testid="verifier-compliance-mention">
          <p className="text-[11px] text-night/75 leading-relaxed">
            <b>Mention de conformité.</b> CONFILEX ne fournit pas de consultation juridique, fiscale, notariale ou comptable personnalisée. Les analyses générées sont purement documentaires, factuelles et organisationnelles.
          </p>
        </div>

        <div className="space-y-3">
          <input type="email" placeholder="Email pour reçu Stripe (optionnel)"
                 value={email} onChange={(e) => setEmail(e.target.value)}
                 className="w-full px-3 py-2 rounded-sm border border-sage bg-ivory text-sm focus:outline-none focus:border-copper"
                 data-testid="verifier-email-input" />
          <label className="flex items-start gap-2 cursor-pointer p-3 rounded-sm bg-paper border border-sage">
            <input type="checkbox" checked={consentAccepted} onChange={(e) => setConsentAccepted(e.target.checked)}
                   className="mt-0.5 accent-petrol" data-testid="verifier-consent" />
            <span className="text-[12px] text-night/80 leading-relaxed">
              J'accepte la clause de non-responsabilité. Je comprends que ce rapport est <b>informatif et factuel</b>,
              et qu'il ne constitue ni un avis juridique, ni un avis fiscal, comptable, notarial ou financier.
            </span>
          </label>
          <label className="flex items-start gap-2 cursor-pointer p-3 rounded-sm bg-paper border border-copper/40">
            <input type="checkbox" checked={waiverAccepted} onChange={(e) => setWaiverAccepted(e.target.checked)}
                   className="mt-0.5 accent-copper" data-testid="verifier-waiver" />
            <span className="text-[12px] text-night/80 leading-relaxed">
              <b>Renonciation expresse au délai de rétractation de 14 jours.</b> Le rapport étant un contenu numérique
              délivré immédiatement, je demande sa génération immédiate et renonce à l'article L221-28 1° du Code de la consommation.
            </span>
          </label>
          {err && <p className="text-[12px] text-bordeaux">{err}</p>}
          <Button
            disabled={!consentAccepted || !waiverAccepted || loading}
            onClick={() => handleUnlock(false)}
            data-testid="verifier-unlock-btn"
            className="w-full sm:w-auto rounded-sm bg-copper text-ivory hover:bg-copper-deep disabled:opacity-40 px-8 py-3"
          >
            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Création du paiement…</>
                     : <>Débloquer l'analyse complète <ArrowRight className="w-4 h-4 ml-2" /></>}
          </Button>

          {isAdmin && (
            <div
              className="mt-4 p-4 rounded-sm border-2 border-emerald-500 bg-emerald-50 shadow-md"
              data-testid="verifier-admin-test-block"
            >
              <div className="flex items-start gap-2 mb-3">
                <ShieldAlert className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5" />
                <div>
                  <p className="text-[12px] uppercase tracking-[0.2em] text-emerald-800 font-bold">
                    🟢 Mode administrateur — Test interne
                  </p>
                  <p className="text-[13px] text-night/80 mt-1 leading-relaxed">
                    Débloquez le rapport <strong>sans passer par Stripe (0 €)</strong>. Réservé aux comptes admin
                    et journalisé pour audit. Le consentement et la renonciation ci-dessus doivent
                    tout de même être cochés.
                  </p>
                </div>
              </div>
              <Button
                type="button"
                disabled={!consentAccepted || !waiverAccepted || loading}
                onClick={() => handleUnlock(true)}
                data-testid="verifier-admin-test-btn"
                className="w-full sm:w-auto rounded-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 font-semibold px-6 py-3 shadow"
              >
                {loading
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Déblocage en cours…</>
                  : <><ShieldAlert className="w-4 h-4 mr-2" />Commander en mode ADMIN_TEST (0 €)</>}
              </Button>
            </div>
          )}
        </div>
      </Card>

      <Card className="p-4 bg-paper border-copper/30 text-[12px] text-night/75 italic leading-relaxed">
        {session.disclaimer}
      </Card>
    </div>
  );
}

function Stat({ n, l }) {
  return (
    <div>
      <p className="font-serif italic text-3xl text-copper">{n}</p>
      <p className="text-[11px] uppercase tracking-[0.18em] text-ivory/65 mt-0.5">{l}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
function UnlockedReport({ session, onPurge, onReset }) {
  const a = session.analysis || {};
  const v = session.vigilance || {};
  const { user } = useAuth();
  const isAuthenticated = !!user?.id;
  const isApprofondi = session.tier === "approfondi";
  const [tab, setTab] = useState("report"); // report | history
  const { ensureStepUp, StepUpDialog } = useStepUp();
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      let qs = "";
      // ADMIN_TEST : bypass step-up (session admin déjà tracée + rôle vérifié)
      const isAdminTestSession = session.payment_status === "ADMIN_TEST";
      if (isAuthenticated && !isAdminTestSession) {
        const stepUp = await ensureStepUp({ purpose: "verifier.download", resourceId: session.session_token });
        if (!stepUp) { setDownloading(false); return; }
        qs = `?step_up_token=${encodeURIComponent(stepUp)}`;
      }
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/verifier/${session.session_token}/report.pdf${qs}`;

      // Téléchargement via fetch + Blob → contourne les bloqueurs de pop-up
      // et garantit le déclenchement du download sur tous les navigateurs.
      try {
        const resp = await fetch(url, { credentials: "include" });
        if (!resp.ok) {
          const detail = await resp.text().catch(() => "");
          throw new Error(`HTTP ${resp.status} ${detail.slice(0, 200)}`);
        }
        const blob = await resp.blob();
        const objectUrl = URL.createObjectURL(blob);
        // Nom du fichier depuis Content-Disposition si présent
        const cd = resp.headers.get("content-disposition") || "";
        const m = /filename\s*=\s*"?([^"]+)"?/i.exec(cd);
        const filename = (m && m[1]) || `confilex-verificateur-${session.session_token.slice(0, 8)}.pdf`;
        const a = document.createElement("a");
        a.href = objectUrl;
        a.download = filename;
        a.rel = "noopener";
        document.body.appendChild(a);
        a.click();
        // Nettoyage
        setTimeout(() => {
          try { document.body.removeChild(a); } catch (e) { /* ignore */ }
          try { URL.revokeObjectURL(objectUrl); } catch (e) { /* ignore */ }
        }, 100);
      } catch (fetchErr) {
        // Fallback : navigation directe (fonctionne même si Blob échoue)
        window.location.href = url;
      }
    } finally {
      setDownloading(false);
    }
  };

  const handlePurgeProtected = async () => {
    const isAdminTestSession = session.payment_status === "ADMIN_TEST";
    if (isAuthenticated && !isAdminTestSession) {
      const stepUp = await ensureStepUp({ purpose: "verifier.purge", resourceId: session.session_token });
      if (!stepUp) return;
    }
    onPurge();
  };

  return (
    <div className="space-y-6" data-testid="verifier-unlocked">
      {StepUpDialog}
      <Card className="p-4 bg-paper border-copper/30 text-[12px] text-night/75 italic">{session.disclaimer}</Card>
      <TraceabilityBanner />

      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-[0.28em] text-copper mb-1 inline-flex items-center gap-2">
            <CheckCircle2 className="w-3.5 h-3.5" /> Rapport débloqué · {session.tier?.toUpperCase()}
          </p>
          <h2 className="font-serif italic text-2xl text-night">{session.doc_type_label} — {session.filename}</h2>
          <FingerprintBadge fingerprint={session.fingerprint} />
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={handleDownload} disabled={downloading} data-testid="verifier-download-btn"
                  className="rounded-sm bg-petrol text-ivory hover:bg-night">
            {downloading
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Vérification…</>
              : <><Download className="w-4 h-4 mr-2" /> Rapport PDF</>}
          </Button>
          <Button variant="outline" onClick={handlePurgeProtected} data-testid="verifier-purge-btn"
                  className="rounded-sm border-bordeaux/30 text-bordeaux hover:bg-bordeaux/5">
            <Trash2 className="w-4 h-4 mr-2" /> Purger
          </Button>
          <Button variant="outline" onClick={onReset} data-testid="verifier-new-btn" className="rounded-sm">Nouvelle analyse</Button>
        </div>
      </div>

      {/* Onglets : Rapport / Historique */}
      <div className="inline-flex border border-sage rounded-sm overflow-hidden text-[12px]" data-testid="verifier-tabs">
        <button
          type="button"
          onClick={() => setTab("report")}
          data-testid="verifier-tab-report"
          className={`px-4 py-2 ${tab === "report" ? "bg-petrol text-ivory" : "text-night/65 hover:bg-paper"}`}
        >
          Rapport
        </button>
        <button
          type="button"
          onClick={() => setTab("history")}
          data-testid="verifier-tab-history"
          className={`px-4 py-2 inline-flex items-center gap-1.5 ${tab === "history" ? "bg-petrol text-ivory" : "text-night/65 hover:bg-paper"}`}
        >
          <History className="w-3.5 h-3.5" /> Historique du dossier
        </button>
      </div>

      {tab === "history" ? (
        <VerifierHistoryPanel sessionToken={session.session_token} />
      ) : (
        <>
          <Card className="p-5 bg-paper border-copper/30">
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="text-[10px] uppercase tracking-[0.28em] text-copper mb-1">Indicateur de complétude documentaire</p>
                <p className="font-serif italic text-xl text-night">{v.label}</p>
              </div>
              <p className="font-serif italic text-3xl text-night">{v.score}<span className="text-base text-night/55">/100</span></p>
            </div>
            <div className="h-1 bg-sage/60 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-petrol to-copper" style={{ width: `${v.score || 0}%` }} />
            </div>
          </Card>

          <Card className="p-5"><h3 className="text-sm font-semibold text-night mb-2">Résumé factuel</h3>
            <p className="text-sm text-night/80 leading-relaxed">{a.summary}</p>
            {a.confidence_overall && <ConfidenceBadge level={a.confidence_overall} className="mt-3" />}
          </Card>

          {/* Stabilo Intelligent — Passages importants */}
          <HighlightsSection highlights={a.highlights} />

          {/* Chronologie */}
          <TimelineSection items={a.timeline} />

          {/* Acteurs identifiés */}
          <ActorsSection items={a.actors} />

          {/* Documents mentionnés */}
          <DocumentsMentionedSection items={a.documents_mentioned} />

          <SectionList title="Faits clés extraits" items={a.key_facts} testid="verifier-key-facts" />
          <SectionList title="Informations qui ne ressortent pas du document" items={a.missing_info} testid="verifier-missing" />
          <SectionList title="Points à clarifier (factuels)" items={a.vigilance_points} testid="verifier-vigilance-points" />
          <SectionList title="Questions à préparer pour un professionnel" items={a.questions_for_expert} testid="verifier-questions" />

          {/* Transmission spécialiste — palier approfondi uniquement */}
          {isApprofondi && (
            <VerifierHandoffPanel session={session} isAuthenticated={isAuthenticated} />
          )}

          {/* Audit transverse — disponible quand on a au moins un rapport débloqué */}
          <Card className="p-6 bg-ivory border-copper/30" data-testid="cta-transverse-audit">
            <p className="text-[10px] uppercase tracking-[0.28em] text-copper mb-2 inline-flex items-center gap-2">
              <GitMerge className="w-3.5 h-3.5" /> AUDIT TRANSVERSE MULTI-PIÈCES
            </p>
            <h3 className="font-serif italic text-xl text-night mb-2">
              Croisez ce rapport avec d'autres pièces du même dossier
            </h3>
            <p className="text-sm text-night/75 mb-4 leading-relaxed">
              Notre moteur déterministe consolide la chronologie, identifie les
              faits corroborés, repère les divergences documentaires et cartographie
              les acteurs entre plusieurs analyses payées. Aucune nouvelle facturation.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
              <div className="flex-1 bg-paper border border-sage rounded-sm px-3 py-2 font-mono text-[11px] text-night/80 overflow-x-auto" data-testid="session-token-display">
                {session.session_token}
              </div>
              <Button
                onClick={() => {
                  try { navigator.clipboard.writeText(session.session_token); } catch (e) { /* ignore */ }
                  try {
                    const pool = JSON.parse(localStorage.getItem("confilex_verifier_tokens_pool") || "[]");
                    const next = Array.isArray(pool) ? pool : [];
                    if (!next.includes(session.session_token)) next.push(session.session_token);
                    localStorage.setItem("confilex_verifier_tokens_pool", JSON.stringify(next.slice(0, 12)));
                  } catch (e) { /* ignore */ }
                  window.open("/audit/transverse", "_blank", "noopener");
                }}
                data-testid="open-transverse-btn"
                className="bg-night text-paper hover:bg-night/90 rounded-sm whitespace-nowrap"
              >
                <GitMerge className="w-4 h-4 mr-2" /> Ouvrir l'audit transverse
              </Button>
            </div>
          </Card>

          {!isApprofondi && (
            <Card className="p-6 bg-night text-ivory border-none">
              <p className="text-[10px] uppercase tracking-[0.28em] text-copper mb-2">Aller plus loin</p>
              <h3 className="font-serif italic text-xl mb-2">Faire valider ces points par un professionnel</h3>
              <p className="text-sm text-ivory/75 mb-4">Les spécialistes référencés CONFILEX peuvent vous accompagner sans frais de mise en relation.</p>
              <div className="flex gap-3 flex-wrap">
                <Button onClick={() => window.location.href = "/our-experts"} data-testid="verifier-cta-internal"
                        className="bg-copper text-ivory hover:bg-copper-deep rounded-sm">
                  Voir les spécialistes référencés <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </Card>
          )}
        </>
      )}

      <Card className="p-4 bg-paper border-copper/30 text-[12px] text-night/75 italic">{session.disclaimer}</Card>
    </div>
  );
}

function SectionList({ title, items, testid }) {
  if (!items || items.length === 0) return null;
  return (
    <Card className="p-5" data-testid={testid}>
      <h3 className="text-sm font-semibold text-night mb-3">{title}</h3>
      <ul className="space-y-2">
        {items.map((it, i) => (
          <li key={i} className="flex gap-2 text-sm text-night/80 leading-relaxed">
            <ShieldCheck className="w-4 h-4 text-copper mt-0.5 shrink-0" />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// V2 — Stabilo Intelligent
// ---------------------------------------------------------------------------
const HIGHLIGHT_CATEGORY_LABELS = {
  clause_sensible: "Clause sensible",
  montant: "Montant",
  delai: "Délai",
  echeance: "Échéance",
  penalite: "Pénalité",
  obligation: "Obligation",
  exclusion: "Exclusion",
  mention_inhabituelle: "Mention inhabituelle",
  anomalie: "Anomalie",
  difference_version: "Différence entre versions",
};

const ACTOR_TYPE_LABELS = {
  personne_physique: "Personne physique",
  societe: "Société",
  administration: "Administration",
  banque: "Banque",
  organisme_social: "Organisme social",
  avocat: "Avocat",
  spécialiste: "Spécialiste",
  notaire: "Notaire",
  huissier: "Huissier",
  autre: "Autre",
};

const DOCUMENT_KIND_LABELS = {
  contrat: "Contrat",
  avenant: "Avenant",
  facture: "Facture",
  devis: "Devis",
  attestation: "Attestation",
  bulletin_paie: "Bulletin de paie",
  tableau_amortissement: "Tableau d'amortissement",
  courrier_recommande: "Courrier recommandé",
  rapport_expertise: "Rapport d'expertise",
  proces_verbal: "Procès-verbal",
  jugement: "Jugement",
  acte_notarie: "Acte notarié",
  autre: "Autre",
};

const TIMELINE_KIND_LABELS = {
  signature: "Signature",
  effet: "Date d'effet",
  renouvellement: "Renouvellement",
  resiliation: "Résiliation",
  recours: "Délai de recours",
  echeance: "Échéance",
  paiement: "Paiement",
  prescription_potentielle: "Prescription potentielle",
  autre: "Autre",
};

function ConfidenceBadge({ level, className = "" }) {
  const styles = {
    haute: "bg-petrol/10 text-petrol border-petrol/30",
    moyenne: "bg-copper/10 text-copper-deep border-copper/30",
    faible: "bg-bordeaux/10 text-bordeaux border-bordeaux/30",
  };
  const labels = { haute: "Confiance élevée", moyenne: "Confiance moyenne", faible: "Confiance faible" };
  const cls = styles[level] || styles.moyenne;
  return (
    <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded-sm border ${cls} ${className}`}>
      <ShieldCheck className="w-3 h-3" />
      {labels[level] || "Confiance moyenne"}
    </span>
  );
}

function HighlightsSection({ highlights }) {
  const [view, setView] = useState("by_category"); // by_category | flat
  if (!highlights || highlights.length === 0) return null;

  const grouped = highlights.reduce((acc, h) => {
    const k = h.category || "mention_inhabituelle";
    if (!acc[k]) acc[k] = [];
    acc[k].push(h);
    return acc;
  }, {});

  return (
    <Card className="p-5" data-testid="verifier-highlights">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-[0.28em] text-copper mb-1 inline-flex items-center gap-2">
            <Highlighter className="w-3.5 h-3.5" /> Stabilo Intelligent
          </p>
          <h3 className="font-serif italic text-xl text-night">Passages importants identifiés ({highlights.length})</h3>
          <p className="text-[11px] text-night/55 italic mt-1">
            Éléments signalés par l'IA à titre informatif. Ne constituent pas un avis professionnel.
          </p>
        </div>
        <div className="inline-flex border border-sage rounded-sm overflow-hidden text-[11px]" data-testid="highlights-view-toggle">
          <button
            type="button"
            onClick={() => setView("by_category")}
            data-testid="highlights-view-categories"
            className={`px-3 py-1.5 ${view === "by_category" ? "bg-petrol text-ivory" : "text-night/65 hover:bg-paper"}`}
          >
            Par catégorie
          </button>
          <button
            type="button"
            onClick={() => setView("flat")}
            data-testid="highlights-view-flat"
            className={`px-3 py-1.5 ${view === "flat" ? "bg-petrol text-ivory" : "text-night/65 hover:bg-paper"}`}
          >
            Liste complète
          </button>
        </div>
      </div>

      {view === "by_category" ? (
        <div className="space-y-5">
          {Object.entries(grouped).map(([cat, items]) => (
            <div key={cat} data-testid={`highlights-cat-${cat}`}>
              <p className="text-[11px] uppercase tracking-[0.22em] text-night/55 mb-2">
                {HIGHLIGHT_CATEGORY_LABELS[cat] || cat} · {items.length}
              </p>
              <div className="space-y-3">
                {items.map((h, i) => <HighlightCard key={i} h={h} />)}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {highlights.map((h, i) => <HighlightCard key={i} h={h} showCategory />)}
        </div>
      )}
    </Card>
  );
}

function HighlightCard({ h, showCategory = false }) {
  return (
    <div className="p-3 rounded-sm border border-sage bg-ivory" data-testid="highlight-card">
      <div className="flex items-start gap-2 mb-2 flex-wrap">
        {showCategory && (
          <span className="text-[10px] uppercase tracking-[0.2em] text-copper px-2 py-0.5 rounded-sm border border-copper/30 bg-paper">
            {HIGHLIGHT_CATEGORY_LABELS[h.category] || h.category}
          </span>
        )}
        <ConfidenceBadge level={h.confidence} />
      </div>
      <blockquote
        className="text-sm leading-relaxed text-night px-1"
        style={{
          background: "linear-gradient(180deg, transparent 55%, rgba(184, 51, 51, 0.22) 55%)",
          fontStyle: "italic",
        }}
      >
        « {h.text} »
      </blockquote>
      {h.rationale && (
        <p className="text-[12px] text-night/65 mt-2 leading-relaxed">{h.rationale}</p>
      )}
    </div>
  );
}

function TimelineSection({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <Card className="p-5" data-testid="verifier-timeline">
      <p className="text-[10px] uppercase tracking-[0.28em] text-copper mb-1 inline-flex items-center gap-2">
        <CalendarDays className="w-3.5 h-3.5" /> Chronologie
      </p>
      <h3 className="font-serif italic text-xl text-night mb-4">Chronologie du document ({items.length})</h3>
      <ol className="relative border-l border-copper/40 ml-2 space-y-5">
        {items.map((t, i) => (
          <li key={i} className="ml-5" data-testid={`timeline-item-${i}`}>
            <span className="absolute -left-[7px] w-3 h-3 rounded-full bg-copper border-2 border-ivory" aria-hidden="true" />
            <p className="text-[11px] uppercase tracking-[0.2em] text-night/55 font-medium">{t.date_iso}</p>
            <p className="text-sm font-semibold text-night mt-0.5">
              {t.label}{" "}
              <span className="text-[11px] uppercase tracking-[0.16em] text-copper-deep font-normal ml-1">
                · {TIMELINE_KIND_LABELS[t.kind] || t.kind}
              </span>
            </p>
            {t.context && <p className="text-[12px] text-night/70 mt-1 leading-relaxed">{t.context}</p>}
          </li>
        ))}
      </ol>
    </Card>
  );
}

function ActorsSection({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <Card className="p-5" data-testid="verifier-actors">
      <p className="text-[10px] uppercase tracking-[0.28em] text-copper mb-1 inline-flex items-center gap-2">
        <Users className="w-3.5 h-3.5" /> Cartographie des acteurs
      </p>
      <h3 className="font-serif italic text-xl text-night mb-4">Acteurs identifiés ({items.length})</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-night/55 border-b border-sage">
              <th className="py-2 pr-3 font-medium">Nom</th>
              <th className="py-2 pr-3 font-medium">Qualité</th>
              <th className="py-2 font-medium">Rôle dans le document</th>
            </tr>
          </thead>
          <tbody>
            {items.map((a, i) => (
              <tr key={i} className="border-b border-sage/50" data-testid={`actor-row-${i}`}>
                <td className="py-2.5 pr-3 font-semibold text-night align-top">{a.name}</td>
                <td className="py-2.5 pr-3 text-copper-deep align-top whitespace-nowrap">
                  {ACTOR_TYPE_LABELS[a.type] || a.type}
                </td>
                <td className="py-2.5 text-night/75 align-top leading-relaxed">{a.role}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function FingerprintBadge({ fingerprint }) {
  if (!fingerprint?.sha256) return null;
  const sha = fingerprint.sha256;
  const short = `${sha.slice(0, 8)}…${sha.slice(-8)}`;
  const size = fingerprint.size_bytes;
  const sizeLabel = size
    ? (size < 1024 ? `${size} o` : `${(size / 1024).toFixed(1)} ko`)
    : null;
  const uploaded = fingerprint.uploaded_at ? new Date(fingerprint.uploaded_at) : null;
  const uploadedLabel = uploaded
    ? uploaded.toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })
    : null;

  const onCopy = (e) => {
    e.preventDefault?.();
    e.stopPropagation?.();
    try { navigator.clipboard.writeText(sha); } catch { /* ignore */ }
  };

  return (
    <div
      className="mt-2 inline-flex items-center gap-2 text-[11px] text-night/55 font-mono flex-wrap"
      data-testid="verifier-fingerprint"
      title={`SHA-256 complet : ${sha}`}
    >
      <Fingerprint className="w-3.5 h-3.5 text-copper" aria-hidden="true" />
      <span>
        Empreinte SHA-256 :{" "}
        <span className="text-night/70 select-text" data-testid="verifier-fingerprint-short">{short}</span>
        {sizeLabel ? <span> · {sizeLabel}</span> : null}
        {uploadedLabel ? <span> · dépôt {uploadedLabel}</span> : null}
      </span>
      <button
        type="button"
        onClick={onCopy}
        className="ml-1 p-0.5 rounded text-night/45 hover:text-copper hover:bg-paper transition-smooth"
        data-testid="verifier-fingerprint-copy"
        title="Copier l'empreinte complète"
        aria-label="Copier l'empreinte SHA-256"
      >
        <CopyIcon className="w-3 h-3" />
      </button>
    </div>
  );
}


function DocumentsMentionedSection({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <Card className="p-5" data-testid="verifier-documents-mentioned">
      <p className="text-[10px] uppercase tracking-[0.28em] text-copper mb-1 inline-flex items-center gap-2">
        <FileStack className="w-3.5 h-3.5" /> Documents identifiés
      </p>
      <h3 className="font-serif italic text-xl text-night mb-4">Documents mentionnés dans le dossier ({items.length})</h3>
      <div className="space-y-3">
        {items.map((d, i) => (
          <div key={i} className="p-3 rounded-sm border border-sage bg-ivory" data-testid={`doc-mention-${i}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap mb-1">
              <p className="text-sm font-semibold text-night">{d.name}</p>
              <span className="text-[10px] uppercase tracking-[0.2em] text-copper px-2 py-0.5 rounded-sm border border-copper/30 bg-paper whitespace-nowrap">
                {DOCUMENT_KIND_LABELS[d.kind] || d.kind}
              </span>
            </div>
            {d.date_iso && <p className="text-[11px] uppercase tracking-[0.18em] text-night/55 mb-1">{d.date_iso}</p>}
            {d.role && <p className="text-[12px] text-night/70 leading-relaxed">{d.role}</p>}
          </div>
        ))}
      </div>
    </Card>
  );
}
